import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiX, FiAlertTriangle } from 'react-icons/fi';
import resourceAllocationService from '../../services/resourceAllocationService';
import projectService from '../../services/projectService';
import userService from '../../services/userService';
import './ResourceAllocationForm.css';

const ResourceAllocationForm = ({ allocationId = null, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [overAllocationWarning, setOverAllocationWarning] = useState(null);
  const [formData, setFormData] = useState({
    project_id: '',
    user_id: '',
    role: '',
    allocation_percentage: 100,
    start_date: '',
    end_date: '',
    billable_rate: '',
    notes: ''
  });

  useEffect(() => {
    loadProjects();
    loadUsers();
    if (allocationId) {
      loadAllocation();
    }
  }, [allocationId]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 100 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await userService.getForDropdown();
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]); // Set empty array on error
    }
  };

  const loadAllocation = async () => {
    try {
      setLoading(true);
      const data = await resourceAllocationService.getById(allocationId);
      setFormData({
        project_id: data.data.project_id || '',
        user_id: data.data.user_id || '',
        role: data.data.role || '',
        allocation_percentage: data.data.allocation_percentage || 100,
        start_date: data.data.start_date ? data.data.start_date.split('T')[0] : '',
        end_date: data.data.end_date ? data.data.end_date.split('T')[0] : '',
        billable_rate: data.data.billable_rate || '',
        notes: data.data.notes || ''
      });
    } catch (error) {
      console.error('Error loading allocation:', error);
      alert('Failed to load allocation');
    } finally {
      setLoading(false);
    }
  };

  const checkOverAllocation = async () => {
    if (!formData.user_id || !formData.start_date) return;

    try {
      const params = {
        user_id: formData.user_id,
        start_date: formData.start_date,
        end_date: formData.end_date || formData.start_date
      };

      const data = await resourceAllocationService.getUtilization(params);
      const userStats = data.data.by_user?.find(u => u.user.id === formData.user_id);

      if (userStats) {
        const currentAllocation = allocationId ?
          userStats.allocations.find(a => a.id !== allocationId)?.allocation_percentage || 0 :
          userStats.utilization_percentage;

        const totalAllocation = currentAllocation + parseInt(formData.allocation_percentage);

        if (totalAllocation > 100) {
          setOverAllocationWarning({
            current: currentAllocation,
            new: totalAllocation,
            message: `This user will be ${totalAllocation}% allocated (over-allocated by ${totalAllocation - 100}%)`
          });
        } else {
          setOverAllocationWarning(null);
        }
      }
    } catch (error) {
      console.error('Error checking allocation:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      checkOverAllocation();
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.user_id, formData.allocation_percentage, formData.start_date, formData.end_date]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = resourceAllocationService.validateAllocationData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      setLoading(true);

      if (allocationId) {
        await resourceAllocationService.update(allocationId, formData);
      } else {
        await resourceAllocationService.create(formData);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/app/resources');
      }
    } catch (error) {
      console.error('Error saving allocation:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Failed to save allocation');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/app/resources');
    }
  };

  return (
    <div className="resource-allocation-form">
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label>Project *</label>
            <select
              name="project_id"
              value={formData.project_id}
              onChange={handleChange}
              className={errors.project_id ? 'error' : ''}
              required
            >
              <option value="">Select Project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            {errors.project_id && <span className="error-message">{errors.project_id}</span>}
          </div>

          <div className="form-group">
            <label>User *</label>
            <select
              name="user_id"
              value={formData.user_id}
              onChange={handleChange}
              className={errors.user_id ? 'error' : ''}
              required
            >
              <option value="">Select User</option>
              {users.map(user => (
                <option key={user.value} value={user.value}>
                  {user.label} ({user.email})
                </option>
              ))}
            </select>
            {errors.user_id && <span className="error-message">{errors.user_id}</span>}
          </div>


         
          <div className="form-group">
            <label>Allocation Percentage * (0-100%)</label>
            <div className="allocation-input">
              <input
                type="number"
                name="allocation_percentage"
                min="0"
                max="100"
                value={formData.allocation_percentage}
                onChange={handleChange}
                className={`allocation-number ${errors.allocation_percentage ? 'error' : ''}`}
                required
              />
              </div>
              <div>
              <input
                type="range"
                name="allocation_percentage"
                min="0"
                max="100"
                step="5"
                value={formData.allocation_percentage}
                onChange={handleChange}
                className={errors.allocation_percentage ? 'error' : ''}
              />
              {/* <span className="percentage-label">%</span> */}
            </div>
            {errors.allocation_percentage && <span className="error-message">{errors.allocation_percentage}</span>}
          </div>

          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className={errors.start_date ? 'error' : ''}
              required
            />
            {errors.start_date && <span className="error-message">{errors.start_date}</span>}
          </div>

          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className={errors.end_date ? 'error' : ''}
            />
            {errors.end_date && <span className="error-message">{errors.end_date}</span>}
            <small className="help-text">Leave empty for ongoing allocation</small>
          </div>

          <div className="form-group">
            <label>Billable Rate (₹/hour)</label>
            <input
              type="number"
              name="billable_rate"
              value={formData.billable_rate}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Role</label>
            <input
              type="text"
              name="role"
              value={formData.role}
              onChange={handleChange}
              placeholder="e.g., Developer, Designer, QA"
            />
          </div>

          <div className="form-group full-width">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Additional notes about this allocation..."
            />
          </div>
        </div>

        {overAllocationWarning && (
          <div className="warning-banner">
            <FiAlertTriangle />
            <div>
              <strong>Over-allocation Warning</strong>
              <p>{overAllocationWarning.message}</p>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
            <FiX /> Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            <FiSave /> {loading ? 'Saving...' : allocationId ? 'Update Allocation' : 'Create Allocation'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResourceAllocationForm;

